import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Lightning, Star, PenNib, Palette, Certificate, ShieldCheck } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { PortfolioSection } from "@/components/PortfolioSection";
import { PublicReviewsCarousel } from "@/components/PublicReviewsCarousel";

// Service catalog — no public pricing (every engagement is quoted individually, RFP-friendly).
const SERVICES = [
  { code: "01", label: "Flyer Design", desc: "Digital + print, fast turnaround." },
  { code: "02", label: "Logo Design", desc: "Custom marks built to last." },
  { code: "03", label: "Seal / Crest", desc: "Heraldic, organizational, ceremonial." },
  { code: "04", label: "Banner Design", desc: "Web, social, large-format." },
  { code: "05", label: "Web Design", desc: "From single-pagers to full sites." },
  { code: "06", label: "Other Designs", desc: "Anything you throw at us." },
];

export default function Landing() {
  const { user } = useAuth();
  const [activeCount, setActiveCount] = useState(null);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const { data } = await api.get("/public/stats");
        if (!cancel) setActiveCount(data.active_projects);
      } catch (_) {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  const ctaTarget = user ? "/dashboard" : "/register";

  return (
    <div className="min-h-screen bg-[#050505] text-[#fafafa] overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#050505]/70 border-b border-[#262626]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-link">
            <img
              src="/vznlogo.png"
              alt="Vision Grafix"
              className="w-[13rem] h-auto object-contain"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#a3a3a3]">
            <a href="#services" className="nav-underline hover:text-white">Services</a>
            <a href="#process" className="nav-underline hover:text-white">Process</a>
            <a href="#credentials" className="nav-underline hover:text-white">Credentials</a>
            <Link to="/portfolio" className="nav-underline hover:text-white" data-testid="nav-portfolio-link">Portfolio</Link>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" data-testid="header-dashboard-btn" className="btn-primary px-4 py-2 text-sm">
                Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="header-login-btn" className="text-sm text-[#a3a3a3] hover:text-white">
                  Sign in
                </Link>
                <Link to="/register" data-testid="header-register-btn" className="btn-primary px-4 py-2 text-sm">
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero — bento split (restored to original) */}
      <section className="relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D] mb-6 hero-fade-up hero-delay-1">
              <span className="inline-block w-8 h-px bg-[#FF2D2D] mr-3 align-middle" />
              Design ops, simplified
            </div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-[8.5rem] font-black leading-[0.85] hero-fade-up hero-delay-2">
              Where Vision<br />
              becomes <span className="hero-accent-shimmer">Reality.</span>
            </h1>
            <p className="text-[#a3a3a3] text-lg max-w-2xl mt-8 hero-fade-up hero-delay-3">
              Submit a request, track production, pay with cash or points, and download finished
              files — all in one place built for serious creative teams and serious clients.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4 hero-fade-up hero-delay-4">
              <Link
                to={ctaTarget}
                data-testid="hero-cta-start"
                className="btn-primary px-7 py-4 text-sm font-bold uppercase tracking-wider flex items-center gap-2"
              >
                Start a request <ArrowUpRight weight="bold" size={18} />
              </Link>
              <a
                href="#services"
                data-testid="hero-cta-services"
                className="btn-outline px-7 py-4 text-sm uppercase tracking-wider"
              >
                See services
              </a>
            </div>
          </div>

          <div className="lg:col-span-4 grid grid-cols-2 gap-4 hero-fade-up hero-delay-5">
            <div className="cell-border bg-[#121212] p-5 col-span-2 relative overflow-hidden mesh-overlay">
              <img
                src="/landing-hero.jpg"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="relative z-10">
                <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF2D2D]">Active queue</div>
                <div
                  className="font-display text-5xl mt-2 text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] tabular-nums min-h-[3rem]"
                  data-testid="active-queue-count"
                >
                  {typeof activeCount === "number" ? activeCount : "—"}
                </div>
                <div className="text-xs text-white/80 mt-1 uppercase tracking-wider">Designs in production right now</div>
              </div>
            </div>
            <div className="cell-border bg-[#121212] p-5">
              <Lightning weight="fill" size={24} className="text-[#FF2D2D]" />
              <div className="font-display text-2xl mt-3">24h</div>
              <div className="text-xs text-[#a3a3a3] mt-1">rush turnaround</div>
            </div>
            <div className="cell-border bg-[#121212] p-5">
              <Star weight="fill" size={24} className="text-[#FF2D2D]" />
              <div className="font-display text-2xl mt-3">5★</div>
              <div className="text-xs text-[#a3a3a3] mt-1">subscriber points</div>
            </div>
          </div>
        </div>

        <div className="marquee-bar">
          <Marquee speed={45} gradient={false}>
            <div className="flex items-center gap-12 py-3 text-sm font-bold uppercase tracking-[0.2em]">
              <span>● Flyers</span>
              <span>● Logos</span>
              <span>● Seals & Crests</span>
              <span>● Banners</span>
              <span>● Web Design</span>
              <span>● Print</span>
              <span>● Branding</span>
              <span>● Video</span>
              <span>● Flyers</span>
              <span>● Logos</span>
              <span>● Seals & Crests</span>
              <span>● Banners</span>
              <span>● Web Design</span>
              <span>● Print</span>
            </div>
          </Marquee>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="border-t border-[#262626]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12">
            <div className="md:col-span-4">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Services</div>
              <h2 className="font-display text-5xl md:text-6xl mt-3 uppercase">Pick a service.<br />Get a custom quote.</h2>
            </div>
            <div className="md:col-span-7 md:col-start-6 text-[#a3a3a3] text-base">
              Every engagement is scoped and quoted individually. Submit a brief through the portal and
              we'll return a written quote your accounting team can hold us to.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
            {SERVICES.map((s) => (
              <Link
                key={s.code}
                to={ctaTarget}
                className="bg-[#0a0a0a] p-8 hover:bg-[#121212] transition-colors group cursor-pointer block"
                data-testid={`service-card-${s.code}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3]">/ {s.code}</span>
                  <ArrowUpRight size={20} className="text-[#a3a3a3] group-hover:text-[#FF2D2D] group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform" />
                </div>
                <div className="font-display text-3xl mb-2">{s.label}</div>
                <div className="text-sm text-[#a3a3a3] mb-6">{s.desc}</div>
                <div className="flex items-baseline gap-2 pt-6 border-t border-[#262626]">
                  <span className="text-xs uppercase tracking-wider text-[#FF2D2D] font-bold">Request a quote →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="border-t border-[#262626] bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">How it works</div>
            <h2 className="font-display text-5xl md:text-6xl mt-3">Six stages.<br />Zero confusion.</h2>
            <p className="text-[#a3a3a3] mt-6 max-w-md">
              Every project moves through a transparent production pipeline — you always know exactly where
              your design is and what's next.
            </p>
            <Link to={ctaTarget} className="btn-primary px-6 py-3 text-sm font-bold uppercase tracking-wider mt-8 inline-flex items-center gap-2" data-testid="process-cta">
              Submit your first vision <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
            {[
              { n: "01", t: "Submitted", d: "Vision received" },
              { n: "02", t: "Preproduction", d: "Concepting" },
              { n: "03", t: "Design", d: "In production" },
              { n: "04", t: "Review", d: "Client review" },
              { n: "05", t: "Final", d: "Polishing" },
              { n: "06", t: "Delivered", d: "Files ready" },
            ].map((stage) => (
              <div key={stage.n} className="bg-[#050505] p-6">
                <div className="text-xs text-[#a3a3a3]">{stage.n}</div>
                <div className="font-display text-2xl mt-2">{stage.t}</div>
                <div className="text-xs text-[#a3a3a3] mt-1">{stage.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials — SWaM / VZN Enterprises · government contracts & RFPs */}
      <section id="credentials" className="border-t border-[#262626]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Government contracts &amp; RFPs</div>
            <h2 className="font-display text-5xl md:text-6xl mt-3 uppercase leading-none">
              Vision &gt; Grafix
            </h2>
            <div className="text-lg text-[#a3a3a3] mt-4">a VZN Enterprises, LLC company</div>
            <p className="text-[#a3a3a3] mt-6 max-w-md">
              Virginia SWaM-certified with 20 years of creative and web development experience. We respond
              to RFPs, hold contract vehicles, and welcome set-aside opportunities for small, women-owned,
              and minority-owned supplier programs.
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
            <div className="bg-[#0a0a0a] p-8" data-testid="credential-swam">
              <Certificate weight="fill" size={32} className="text-[#FF2D2D]" />
              <div className="font-display text-3xl mt-4">SWaM-Certified</div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-2">Virginia · Small, Women-owned, Minority</div>
              <p className="text-sm text-[#a3a3a3] mt-4">
                Eligible for Virginia state contracts, set-aside opportunities, and diverse-supplier programs.
              </p>
            </div>
            <div className="bg-[#0a0a0a] p-8" data-testid="credential-years">
              <ShieldCheck weight="fill" size={32} className="text-[#FF2D2D]" />
              <div className="font-display text-3xl mt-4">20 Years</div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-2">Creative &amp; web development</div>
              <p className="text-sm text-[#a3a3a3] mt-4">
                Two decades of production experience across print, digital, branding, and full-stack web.
              </p>
            </div>
            <div className="bg-[#0a0a0a] p-8 sm:col-span-2" data-testid="credential-legal">
              <div className="text-xs uppercase tracking-[0.3em] text-[#a3a3a3]">Legal entity</div>
              <div className="font-display text-2xl mt-2">VZN Enterprises, LLC</div>
              <div className="text-sm text-[#a3a3a3] mt-2">
                Registered in the Commonwealth of Virginia. Ask about our capabilities statement for RFP submissions.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio (renders only when items exist) */}
      <PortfolioSection limit={3} teaser />

      <PublicReviewsCarousel />

      {/* CTA */}
      <section className="border-t border-[#262626] bg-[#FF2D2D] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <h2 className="font-display text-5xl md:text-7xl uppercase">Got a vision?<br />Let's make it real.</h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to={ctaTarget}
              data-testid="footer-cta"
              className="bg-[#050505] text-[#FF2D2D] px-8 py-4 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 hover:bg-[#1a1a1a] transition-colors"
            >
              Open the Client Portal <ArrowUpRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#262626]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#a3a3a3]">
          <div className="flex items-center gap-3">
            <img
              src="/vznlogo.png"
              alt="Vision Grafix"
              className="w-[11rem] md:w-[13rem] h-auto object-contain"
            />
            <span>© 2026 — Design operations system</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/press" className="hover:text-[#FF2D2D] uppercase tracking-wider" data-testid="footer-press-link">Press</Link>
            <PenNib size={14} /> <Palette size={14} /> <Star size={14} weight="fill" className="text-[#FF2D2D]" />
          </div>
        </div>
      </footer>
    </div>
  );
}
